import { ColumnDimension } from "@components/common/data-grid/adm-data-grid-types";
import {
  HARDWARE_TABLE_COLUMN_LABELS,
  HardwareTableColumnField,
  HardwareTableGridColDef,
} from "@components/plans-table/hardware-table-types";
import { formatPlanTableName } from "@utils/create-plan-utils";
import {
  PlanAssignedToCell,
  PlanAssigneeNameCell,
  PlanExpirationDateCell,
  PlanNameCell,
  PlanOptionsCell,
  PlanStartDateCell,
  PlanStatusCell,
} from "@utils/data-grid-utils/plans-data-grid-cells";
import { useMemo } from "react";
import { sortByPlanStatus } from "@components/plans-table/custom-sorting";
import { statusColumnFilterOperators } from "@components/plans-table/plans-table-filter";

const columnDimension: ColumnDimension<HardwareTableColumnField> = {
  licenseName: {
    flex: 1.5,
  },
  status: {
    flex: 1.0,
  },
  assignedTo: {
    flex: 1,
  },
  serialNumber: {
    flex: 1,
  },
  startDate: {
    flex: 1,
  },
  expirationDate: {
    flex: 1,
  },
  options: {
    flex: 1,
    maxWidth: 120,
  },
};

/**
 * @returns the columns definition to be used in the hardware table
 */
export function useHardwareTableColumns(): HardwareTableGridColDef[] {
  const columnsMap: Record<HardwareTableColumnField, HardwareTableGridColDef> =
    useMemo(() => {
      return {
        licenseName: {
          field: "licenseName",
          headerName: HARDWARE_TABLE_COLUMN_LABELS.licenseName,
          ...columnDimension.licenseName,
          valueGetter: (_, row) => formatPlanTableName(row.entity),
          renderCell: PlanNameCell,
        },

        status: {
          field: "status",
          headerName: HARDWARE_TABLE_COLUMN_LABELS.status,
          ...columnDimension.status,
          sortComparator: sortByPlanStatus,
          valueGetter: (_, row) => row.entity.status.identifier,
          renderCell: PlanStatusCell,
          // Needed by the Status dropdown, which matches a plan against any of the selected statuses
          filterOperators: statusColumnFilterOperators,
        },

        assignedTo: {
          field: "assignedTo",
          headerName: HARDWARE_TABLE_COLUMN_LABELS.assignedTo,
          ...columnDimension.assignedTo,
          valueGetter: (_, row) => row.entity.subject.type,
          renderCell: PlanAssignedToCell,
        },

        serialNumber: {
          field: "serialNumber",
          headerName: HARDWARE_TABLE_COLUMN_LABELS.serialNumber,
          ...columnDimension.serialNumber,
          renderCell: PlanAssigneeNameCell,
        },

        startDate: {
          field: "startDate",
          headerName: HARDWARE_TABLE_COLUMN_LABELS.startDate,
          ...columnDimension.startDate,
          valueGetter: (_, row) => row.entity.startDate,
          renderCell: PlanStartDateCell,
        },

        expirationDate: {
          field: "expirationDate",
          headerName: HARDWARE_TABLE_COLUMN_LABELS.expirationDate,
          ...columnDimension.expirationDate,
          valueGetter: (_, row) => row.entity.endDate,
          renderCell: PlanExpirationDateCell,
        },

        options: {
          field: "options",
          type: "actions",
          headerName: HARDWARE_TABLE_COLUMN_LABELS.options,
          ...columnDimension.options,
          getApplyQuickFilterFn: () => null,
          align: "right",
          renderCell: PlanOptionsCell,
        },
      };
    }, []);

  return Object.values(columnsMap);
}
